// src/components/Enablement/Moodle/Quiz/QuestionHTML.tsx
import React, { useMemo, useEffect } from 'react';
import DOMPurify from 'dompurify';

type Props = {
  html: string;
  hideFlagUI?: boolean;
  hideEmbeddedNav?: boolean;
};

export default function QuestionHTML({ html, hideFlagUI = true, hideEmbeddedNav = true }: Props) {
  const cleaned = useMemo(() => {
    const base = DOMPurify.sanitize(html || '', {
      USE_PROFILES: { html: true },
      FORBID_TAGS: ['script', 'style', 'link', 'iframe', 'embed', 'object'],
      FORBID_ATTR: ['on*'],
      ADD_ATTR: ['target', 'rel', 'aria-label', 'aria-labelledby', 'aria-describedby', 'role'],
      ALLOW_DATA_ATTR: false,
    });

    const parser = new DOMParser();
    const doc = parser.parseFromString(base, 'text/html');

    // Remove unwanted UI elements but preserve structure
    if (hideFlagUI) {
      const unwantedSelectors = [
        '.questionflag', '.questionflagimage',
        '[id$=":flaggedlabel"]', '[id$=":flaggedcheckbox"]',
        '.state', // "Not complete" status
        '.badge' // version badges
      ];
      unwantedSelectors.forEach(selector => {
        doc.querySelectorAll(selector).forEach(el => el.remove());
      });
    }

    // Remove navigation/submit elements
    if (hideEmbeddedNav) {
      const navSelectors = [
        '.submitbtns', '.navigation', '.paging',
        'button[type="submit"]', 'input[type="submit"]',
        '.im-controls',
        '.qtype_multichoice_clearchoice', // Clear choice section
        '.sr-only:not(.prompt)', '.accesshide' // Keep prompt but remove other screen reader elements
      ];
      navSelectors.forEach(selector => {
        doc.querySelectorAll(selector).forEach(el => el.remove());
      });
    }

    // Remove the default selected "Clear my choice" radio button
    doc.querySelectorAll('input[type="radio"][value="-1"][checked]').forEach((radio) => {
      (radio as HTMLInputElement).checked = false;
      radio.removeAttribute('checked');
    });

    // Make answer options clickable by wrapping in labels
    const answerDiv = doc.querySelector('.answer');
    if (answerDiv) {
      const answerRows = answerDiv.querySelectorAll('.r0, .r1');
      answerRows.forEach((row) => {
        const radio = row.querySelector('input[type="radio"]');
        if (radio && radio.id) {
          // Create a label that wraps the entire row content
          const label = doc.createElement('label');
          label.setAttribute('for', radio.id);
          label.className = 'answer-option-label';

          // Move all content from row to label
          while (row.firstChild) {
            label.appendChild(row.firstChild);
          }

          // Replace row with label
          row.parentNode?.replaceChild(label, row);
        }
      });
    }

    // Add classes for styling without further restructuring HTML
    const questionDiv = doc.querySelector('.que');
    if (questionDiv) {
      questionDiv.classList.add('enhanced-question');
    }

    // Clean up empty paragraphs
    doc.querySelectorAll('p').forEach(p => {
      if (!p.textContent?.trim()) {
        p.remove();
      }
    });

    // Enhance images
    doc.querySelectorAll('img').forEach((img) => {
      (img as HTMLImageElement).style.maxWidth = '100%';
      (img as HTMLImageElement).style.height = 'auto';
    });

    // Defense in depth: re-sanitize the post-DOMParser output. The DOMParser
    // block above is a cosmetic cleaner, not a sanitizer.
    return DOMPurify.sanitize(doc.body.innerHTML, {
      USE_PROFILES: { html: true },
      FORBID_TAGS: ['script', 'style', 'link', 'iframe', 'embed', 'object'],
      FORBID_ATTR: ['on*'],
      ADD_ATTR: ['target', 'rel', 'aria-label', 'aria-labelledby', 'aria-describedby', 'role'],
    });
  }, [html, hideFlagUI, hideEmbeddedNav]);

  // Add click handlers after component mounts
  useEffect(() => {
    const addClickHandlers = () => {
      // Find all answer option containers that don't have labels
      const answerOptions = document.querySelectorAll('.enhanced-question .answer > div:not(.answer-option-label)');

      answerOptions.forEach((option) => {
        const radio = option.querySelector('input[type="radio"]') as HTMLInputElement;
        if (radio) {
          // Add click handler to the entire option
          option.addEventListener('click', (e) => {
            // Don't double-trigger if clicking directly on the radio button
            if (e.target === radio) return;

            radio.checked = true;
            // Trigger change event for form handling
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          });

          // Add cursor pointer style
          (option as HTMLElement).style.cursor = 'pointer';
        }
      });
    };

    // Add handlers after a short delay to ensure DOM is ready
    const timer = setTimeout(addClickHandlers, 100);

    return () => clearTimeout(timer);
  }, [cleaned]); // Re-run when content changes

  // Add styles that work with the original Moodle structure
  useEffect(() => {
    const styleId = 'enhanced-moodle-styles';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      /* Enhanced styles for original Moodle structure */
      
      .enhanced-question {
        background: #fff;
        border: 1px solid #e0e0e0;
        border-radius: 12px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.06);
        overflow: hidden;
        margin-bottom: 1.5rem;
      }
      
      .enhanced-question .info {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.75rem 1.25rem;
        background-color: #f9fafb;
        border-bottom: 1px solid #e0e0e0;
      }
      
      .enhanced-question .info .no {
        font-weight: 700;
        font-size: 1rem;
        color: #333;
        margin: 0;
      }
      
      .enhanced-question .content {
        padding: 1.25rem;
      }
      
      .enhanced-question .qtext {
        font-size: 1.1rem;
        line-height: 1.6;
        margin-bottom: 1.5rem;
        color: #333;
      }
      
      .enhanced-question .qtext p {
        margin-bottom: 1rem;
      }
      
      .enhanced-question .qtext p:last-child {
        margin-bottom: 0;
      }
      
      .enhanced-question .challenge {
        font-weight: 600;
        color: #1f2937;
      }
      
      /* Answer section styling */
      .enhanced-question .answer {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
      }
      
      /* Individual answer row styling - both div and label variants */
      .enhanced-question .answer > div,
      .enhanced-question .answer > .answer-option-label {
        display: flex;
        align-items: flex-start;
        gap: 0.75rem;
        padding: 1rem;
        border: 2px solid #e5e7eb;
        border-radius: 10px;
        background: #fff;
        transition: all 0.2s ease;
        cursor: pointer;
      }
      
      .enhanced-question .answer > div:hover,
      .enhanced-question .answer > .answer-option-label:hover {
        background-color: #f8faff;
        border-color: #3b82f6;
      }
      
      /* Radio button styling */
      .enhanced-question .answer input[type="radio"] {
        margin: 0.25rem 0 0 0;
        width: 1.25rem;
        height: 1.25rem;
        flex-shrink: 0;
        accent-color: #3b82f6;
      }
      
      /* Answer label styling */
      .enhanced-question .answer [data-region="answer-label"] {
        flex: 1;
        display: flex;
        align-items: flex-start;
        gap: 0.5rem;
      }
      
      .enhanced-question .answer .answernumber {
        font-weight: 600;
        color: #4b5563;
        flex-shrink: 0;
      }
      
      .enhanced-question .answer .flex-fill {
        flex: 1;
      }
      
      .enhanced-question .answer .flex-fill p {
        margin: 0;
        font-size: 1rem;
        line-height: 1.5;
        color: #374151;
      }
      
      /* Selected state */
      .enhanced-question .answer > div:has(input[type="radio"]:checked),
      .enhanced-question .answer > .answer-option-label:has(input[type="radio"]:checked) {
        border-color: #3b82f6;
        background-color: #eff6ff;
      }
      
      /* Code styling */
      .enhanced-question code {
        background: #f3f4f6;
        padding: 0.125rem 0.375rem;
        border-radius: 0.25rem;
        font-family: 'Monaco', 'Consolas', 'Ubuntu Mono', monospace;
        font-size: 0.9em;
      }
      
      /* Glossary link styling */
      .enhanced-question a.glossary {
        color: #3b82f6;
        text-decoration: none;
        border-bottom: 1px dotted #3b82f6;
      }
      
      .enhanced-question a.glossary:hover {
        text-decoration: underline;
      }
      
      /* Hide unwanted elements */
      .enhanced-question .sr-only,
      .enhanced-question .accesshide,
      .enhanced-question [aria-hidden="true"] {
        display: none !important;
      }
      
      /* Fieldset cleanup */
      .enhanced-question fieldset {
        border: none;
        padding: 0;
        margin: 0;
      }
      
      .enhanced-question legend {
        display: none;
      }
      
      /* Ensure proper cursor for all clickable elements */
      .enhanced-question .answer > div,
      .enhanced-question .answer > .answer-option-label {
        cursor: pointer !important;
      }
      
      /* Prevent text selection on option containers to make clicking feel more button-like */
      .enhanced-question .answer > div,
      .enhanced-question .answer > .answer-option-label {
        user-select: none;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
      }
      
      /* But allow text selection on the actual text content */
      .enhanced-question .answer .flex-fill {
        user-select: text;
        -webkit-user-select: text;
        -moz-user-select: text;
        -ms-user-select: text;
      }
    `;

    document.head.appendChild(style);

    return () => {
      document.getElementById(styleId)?.remove();
    };
  }, []);

  return <div className="moodle-question" dangerouslySetInnerHTML={{ __html: cleaned }} />;
}